import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "@/lib/api";
import type { AccessState, AdminUser } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Chip } from "@/ui/Chip";
import { Button } from "@/ui/Button";
import { Input } from "@/ui/Field";
import { EmptyState, ErrorState, Skeleton } from "@/ui/States";
import { useToast } from "@/ui/Toast";
import { absoluteTime, relativeTime } from "@/lib/format";

export const Route = createFileRoute("/_app/admin/access")({
  component: BetaAccessScreen,
});

/**
 * The queue of people waiting to be let in.
 *
 * Oldest first, deliberately: a queue people are standing in should be served
 * in the order they joined it, and a list sorted newest-first quietly starves
 * whoever has been waiting longest.
 *
 * Each decision is one action with the message attached, because the message is
 * not a note to ourselves — it is sent. Somebody who registered and heard
 * nothing registers again, and then writes to support.
 */
function BetaAccessScreen() {
  const toast = useToast();
  const queue = useQuery({
    queryKey: ["access-queue"],
    queryFn: ({ signal }) =>
      api.get<{ users: AdminUser[] | null }>(
        "/admin/users",
        { access: "pending", limit: "200" },
        signal,
      ),
    refetchInterval: 30000,
  });

  const waiting = [...(queue.data?.users ?? [])].sort(
    (a, b) => +new Date(a.created_at) - +new Date(b.created_at),
  );

  return (
    <div className="flex flex-col gap-6">
      {queue.isLoading ? (
        <Skeleton className="h-48 w-full rounded-xl" />
      ) : queue.isError ? (
        <ErrorState error={queue.error} onRetry={() => void queue.refetch()} />
      ) : waiting.length === 0 ? (
        <Panel>
          <EmptyState
            title="Nobody is waiting"
            description="Every account that has registered has been decided on."
          />
        </Panel>
      ) : (
        <div className="flex flex-col gap-3">
          {waiting.map((u) => (
            <Applicant key={u.id} user={u} onDecided={() => void queue.refetch()} toast={toast} />
          ))}
        </div>
      )}
    </div>
  );
}

function Applicant({
  user,
  onDecided,
  toast,
}: {
  user: AdminUser;
  onDecided: () => void;
  toast: ReturnType<typeof useToast>;
}) {
  const [note, setNote] = useState("");

  const decide = useMutation({
    mutationFn: (access: AccessState) =>
      api.post<void>(`/admin/users/${user.id}/access`, { access, note: note.trim() }),
    onSuccess: (_, access) => {
      toast.success(
        access === "approved" ? `${user.email} is in` : `${user.email} was turned down`,
        "They have been emailed.",
      );
      onDecided();
    },
    onError: toast.error,
  });

  return (
    <Panel>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/admin/users/$userId"
              params={{ userId: user.id }}
              className="font-medium text-[var(--text-primary)] hover:underline"
            >
              {user.email}
            </Link>
            {!user.email_verified && <Chip tone="warn">Address unconfirmed</Chip>}
          </div>
          <p className="mt-0.5 text-[13px] text-[var(--text-muted)]">
            {user.display_name ? `${user.display_name} · ` : ""}
            <span title={absoluteTime(user.created_at)}>
              waiting {relativeTime(user.created_at).replace(" ago", "")}
            </span>
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={note}
            placeholder="What to tell them"
            className="h-9 w-56 text-[13px]"
            onChange={(e) => setNote(e.target.value)}
          />
          <Button
            tone="primary"
            size="dense"
            busy={decide.isPending && decide.variables === "approved"}
            onClick={() => decide.mutate("approved")}
          >
            Approve
          </Button>
          <Button
            tone="ghost"
            size="dense"
            busy={decide.isPending && decide.variables === "rejected"}
            onClick={() => decide.mutate("rejected")}
          >
            Turn down
          </Button>
        </div>
      </div>

      {/* An unconfirmed address is not a reason to refuse, but it is a reason to
          look twice: the approval email will go somewhere nobody has proven they
          can read. */}
      {!user.email_verified && (
        <p className="mt-3 text-[13px] text-[var(--warning)]">
          This address has not been confirmed. Approving sends an email to an address nobody has
          proven they can read.
        </p>
      )}
    </Panel>
  );
}
